import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, LinearProgress, Stack,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { episodesAPI, patreonPostAPI, PublishActivityItem } from '../../services/api';

const STATUS_META: Record<string, { icon: string; color: string; label: string }> = {
  in_progress: { icon: '⏳', color: 'warning.main', label: 'In progress' },
  published: { icon: '✅', color: 'success.main', label: 'Published' },
  error: { icon: '❌', color: 'error.main', label: 'Error' },
};

const PLATFORM_COLOR: Record<string, string> = {
  Patreon: '#f96854', Megaphone: '#3b82f6', YouTube: '#ef4444', WordPress: '#21759b',
};

const fmtDate = (s: string | null): string => {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return s; }
};

const Row: React.FC<{ item: PublishActivityItem; onResumed: () => void }> = ({ item, onResumed }) => {
  const [busy, setBusy] = useState(false);
  const [resumeErr, setResumeErr] = useState<string | null>(null);
  const sm = STATUS_META[item.status] || STATUS_META.published;
  const resume = async () => {
    if (!item.resume_job_id) return;
    setBusy(true); setResumeErr(null);
    try { await patreonPostAPI.retryJob(item.resume_job_id); onResumed(); }
    catch (e: any) { setResumeErr(e?.response?.data?.detail || e?.message || 'resume failed'); }
    finally { setBusy(false); }
  };
  return (
    <Box sx={{
      display: 'flex', gap: 2, py: 1.5, alignItems: 'flex-start',
      borderBottom: '1px solid', borderColor: 'divider',
    }}>
      {item.thumbnail_url
        ? <Avatar variant="rounded" src={item.thumbnail_url} sx={{ width: 48, height: 48 }} />
        : <Box sx={{ width: 48, textAlign: 'center', fontSize: 24, pt: 0.5 }}>{sm.icon}</Box>}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" color="text.secondary">{fmtDate(item.when)}</Typography>
          {item.platforms.map((p) => (
            <Chip key={p} size="small" label={p}
              sx={{ bgcolor: PLATFORM_COLOR[p] || 'grey.700', color: '#fff', height: 18, fontSize: 11 }} />
          ))}
          <Typography variant="caption" sx={{ color: sm.color }}>
            {sm.icon} {sm.label}
            {item.status === 'in_progress' && item.progress_pct != null ? ` ${item.progress_pct}%` : ''}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.3 }} noWrap>{item.title}</Typography>
        {item.subtitle && (
          <Typography variant="caption" color="text.secondary">{item.subtitle}</Typography>
        )}
        {item.status === 'in_progress' && item.progress_pct != null && (
          <LinearProgress variant="determinate" value={item.progress_pct} sx={{ mt: 0.5, maxWidth: 320 }} />
        )}
        {(item as any).note && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
            {(item as any).note}
          </Typography>
        )}
        {item.error && (
          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.3 }}>
            {item.error}
          </Typography>
        )}
        {resumeErr && (
          <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.3 }}>
            Resume failed: {resumeErr}
          </Typography>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 0.6 }} flexWrap="wrap">
          {item.resume_job_id && (
            <Button size="small" variant="contained" color="warning" onClick={resume} disabled={busy}
              startIcon={busy ? <CircularProgress size={12} /> : undefined}>
              {busy ? 'Resuming…' : 'Resume'}
            </Button>
          )}
          {item.edit_wp_url && (
            <Button size="small" variant="outlined" component="a"
              href={item.edit_wp_url} target="_blank" rel="noopener">Edit (WP)</Button>
          )}
          {item.views.map((v) => (
            <Button key={v.url + v.label} size="small" component="a"
              href={v.url} target="_blank" rel="noopener">{v.label} ↗</Button>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

type Filter = 'all' | 'attention' | 'patreon' | 'episode';

const PublishActivityFeed: React.FC = () => {
  const [items, setItems] = useState<PublishActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  const load = useCallback(async () => {
    try {
      const r = await episodesAPI.publishActivity(80);
      setItems(r.items || []); setErr(null);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'failed to load activity');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // keep partway jobs live without a manual refresh
  useEffect(() => {
    if (!items.some((i) => i.status === 'in_progress')) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [items, load]);

  const shown = items.filter((i) =>
    filter === 'all' ? true
      : filter === 'attention' ? (i.status === 'in_progress' || i.status === 'error')
        : i.kind === filter);

  const attentionCount = items.filter((i) => i.status === 'in_progress' || i.status === 'error').length;

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6">Everything published through the app</Typography>
          <Typography variant="caption" color="text.secondary">
            Patreon posts + podcast episodes, newest first. Resume anything partway, open anything released.
          </Typography>
        </Box>
        <Button size="small" onClick={load}>↻ Refresh</Button>
      </Stack>
      <ToggleButtonGroup size="small" exclusive value={filter}
        onChange={(_, v) => v && setFilter(v)} sx={{ mb: 1 }}>
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="attention">
          Needs attention{attentionCount ? ` (${attentionCount})` : ''}
        </ToggleButton>
        <ToggleButton value="patreon">Patreon</ToggleButton>
        <ToggleButton value="episode">Episodes</ToggleButton>
      </ToggleButtonGroup>
      {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
      ) : shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>Nothing here yet.</Typography>
      ) : (
        shown.map((it) => <Row key={it.id} item={it} onResumed={load} />)
      )}
    </Box>
  );
};

export default PublishActivityFeed;
